import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ownerApi, errText } from "@/lib/ownerApi";
import ReconHeadline from "./ReconHeadline";
import ReconBatchTable from "./ReconBatchTable";
import ReconPayments from "./ReconPayments";
import ReconRevised from "./ReconRevised";
import TransferDecisions from "./TransferDecisions";

const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

const History = ({ rows, sel, onPick }) => (
  <div className="flex flex-wrap gap-2" data-testid="recon-history">
    {rows.map((r) => (
      <button key={r.id} onClick={() => onPick(r.id)} data-testid={`recon-import-${r.id}`}
        className={`text-left rounded-lg border px-3 py-2 text-xs transition-colors ${sel === r.id ? "border-emerald-700 bg-emerald-50" : "border-stone-200 bg-white hover:bg-stone-50"}`}>
        <div className="font-semibold text-stone-800 flex items-center gap-1"><FileSpreadsheet className="h-3.5 w-3.5" />{r.filename}</div>
        <div className="text-stone-500">{fmt(r.created_at)} · <span className={r.status === "confirmed" ? "text-emerald-700 font-semibold" : ""}>{r.status}</span></div>
      </button>
    ))}
  </div>
);

export default function Reconcile() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadList = useCallback(() => ownerApi.get("/fin/imports").then(({ data }) => { setList(data); return data; }), []);
  const open = useCallback((id) => { setSel(id); setD(null); ownerApi.get(`/fin/imports/${id}`).then(({ data }) => setD(data)).catch((e) => toast.error(errText(e))); }, []);
  useEffect(() => { loadList().then((rows) => rows[0] && open(rows[0].id)).catch((e) => toast.error(errText(e))); }, [loadList, open]);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try { const { data } = await ownerApi.post("/fin/import/preview", fd); await loadList(); open(data.id); toast.success("Preview ready. Nothing is saved until you confirm."); }
    catch (er) { toast.error(errText(er)); } finally { setBusy(false); e.target.value = ""; }
  };
  const confirm = async () => {
    setBusy(true);
    try { const { data } = await ownerApi.post(`/fin/imports/${sel}/confirm`); toast.success(`Imported ${data.students} students, ${data.payments} payments`); await loadList(); open(sel); }
    catch (er) { toast.error(errText(er)); } finally { setBusy(false); }
  };
  const decide = async (ref, decision) => {
    try { await ownerApi.post("/fin/decisions", { import_id: sel, ref, decision }); setD((x) => ({ ...x, decisions: { ...x.decisions, [ref]: { decision } } })); }
    catch (er) { toast.error(errText(er)); }
  };
  return (
    <div className="space-y-5" data-testid="owner-recon">
      <TransferDecisions />
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 rounded-md bg-emerald-800 text-white px-4 h-10 text-sm font-semibold cursor-pointer hover:bg-emerald-900" data-testid="recon-upload">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload fees.xlsx (preview)
          <input type="file" accept=".xlsx" className="hidden" onChange={upload} disabled={busy} data-testid="recon-upload-input" />
        </label>
        <p className="text-xs text-stone-500 max-w-xl">Step 1: the file is read and checked against the website. Nothing is saved as history until you press Confirm. Website fees and receipts are never changed.</p>
      </div>
      {list.length > 0 && <History rows={list} sel={sel} onPick={open} />}
      {sel && !d && <Loader2 className="h-6 w-6 animate-spin text-emerald-800 mx-auto" />}
      {d && (
        <>
          <ReconHeadline h={d.report.headline} problems={d.report.problems} />
          {d.report.revised && <ReconRevised r={d.report} before={d.report.headline.excel_rows} />}
          <ReconPayments p={d.report.payments} expenses={d.report.expense_estimates} />
          <ReconBatchTable batches={d.report.batches} decisions={d.decisions || {}} onDecide={decide} twice={d.report.website_matched_twice} />
          <div className="rounded-2xl border border-stone-200 bg-white p-4 flex flex-wrap items-center gap-3" data-testid="recon-confirm-box">
            {d.status === "confirmed" ? <p className="text-emerald-800 font-semibold flex items-center gap-2" data-testid="recon-confirmed"><CheckCircle2 className="h-5 w-5" /> Imported as history on {fmt(d.confirmed_at)}</p> : (
              <>
                <p className="text-sm text-stone-600 flex-1">{d.already_confirmed_as ? "This exact file is already imported — confirming again is blocked to avoid duplicates." : `Status: ${d.status}. Confirm saves the Excel students, teacher payments and expense estimates as historical records (previous import is kept, marked superseded).`}</p>
                {d.status === "preview" && !d.already_confirmed_as && <Button onClick={confirm} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="recon-confirm">Confirm import</Button>}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
